import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { HikvisionCameraService } from '@zaoblako/web/core/services/passage/hikvision-camera.service';
import { HikvisionCameraFindInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera/hikvision-camera-find.dto';
import {
  HikvisionCameraFormDto,
  type HikvisionCameraFormInterface
} from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera/hikvision-camera-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { CarCategorySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/car-category-single-select/car-category-single-select.component';
import { RoomMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-multi-select/room-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { HikvisionCameraDirectionEnum } from '@zaoblako/shared/modules/cctv/enums/hikvision-camera-direction.enum';
import { HikvisionCameraDirectionNamePipe } from '@zaoblako/web/shared/pipes/hikvision-camera-direction-name.pipe';
import { ConverterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/converter-single-select/converter-single-select.component';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'crm-hikvision-camera-form',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    SpinnerComponent,
    ButtonModule,
    JoinPipe,
    InputTextComponent,
    DividerModule,
    CarCategorySingleSelectComponent,
    ClientAutoCompleteComponent,
    RoomMultiSelectComponent,
    FormsModule,
    CheckboxModule,
    SelectButtonModule,
    InputNumberModule,
    ConverterSingleSelectComponent
  ],
  templateUrl: './hikvision-camera-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HikvisionCameraFormComponent extends BaseFormComponent<HikvisionCameraFormInterface> implements OnDestroy, OnInit {
  @Input() public readonly index: number;
  public camera = <HikvisionCameraFormInterface>{
    direction: HikvisionCameraDirectionEnum.ENTRY
  };

  public dto = HikvisionCameraFormDto;

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;
  public engine: 'class-transformer' | 'serde' = 'serde';

  private formSubscription$: Subscription = new Subscription();

  public directionOptions: { label: string; value: HikvisionCameraDirectionEnum; }[] = Object
    .values(HikvisionCameraDirectionEnum)
    .map((value) => ({
      value,
      label: (new HikvisionCameraDirectionNamePipe()).transform(value)
    }));

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly hikvisionCameraService: HikvisionCameraService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public ngOnDestroy(): void {
    this.formSubscription$?.unsubscribe();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.hikvisionCameraService
      .findById<HikvisionCameraFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (camera) => {
          this.camera = camera;

          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveCamera() {
    if (this.checkValidate(this.camera).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.hikvisionCameraService.update<HikvisionCameraFormInterface, HikvisionCameraFindInterface>(this.camera).subscribe({
          next: (camera) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Камера "${camera?.name}" обновлена`);
            this.formStoreService.saveNext(camera, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.hikvisionCameraService.create<HikvisionCameraFindInterface>(this.camera).subscribe({
          next: (camera) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Камера обновлена');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/passage/hikvision-camera/card/' + camera.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(camera, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteCamera() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить камеру "${this.camera?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.hikvisionCameraService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe(() => {
          this.errorService.addSuccessMessage(`Камера "${this.camera?.name}" удалена`);
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }
}
